import { ObservableValue } from "azure-devops-ui/Core/Observable";
import { IProjectContextProvider } from "../../common/types";
import { Persona } from "../../common/clients/ogg";
import { DefaultDefinitionOfReady } from "../../common/components/projectSettings/dor/defaultDefinitionOfReady";

const samplePersonas: Persona[] = [
  {
    "name": "End User",
    "description": "A typical consumer of the SaaS product, primarily focused on utilizing its features to solve specific problems or enhance productivity in their day-to-day tasks."
  },
  {
    "name": "System Administrator",
    "description": "Responsible for the overall management of the SaaS product within an organization, including user management, security settings, and ensuring smooth operation and integration with other tools."
  },
  {
    "name": "Marketing Specialist",
    "description": "Focuses on leveraging the SaaS product to execute marketing campaigns, analyze market trends, and manage customer relationships through various channels."
  },
  {
    "name": "Sales Manager",
    "description": "Uses the SaaS product to monitor sales performance, manage sales teams, and develop strategies for achieving sales targets."
  },
  {
    "name": "Customer Support Representative",
    "description": "Provides support to users of the SaaS product, solving technical issues, guiding users through features, and ensuring customer satisfaction."
  },
  {
    "name": "Product Manager",
    "description": "Oversees the development and improvement of the SaaS product, gathering user feedback, prioritizing feature development, and aligning product strategies with business goals."
  },
  {
    "name": "IT Security Specialist",
    "description": "Ensures the security of the SaaS product, protecting against cyber threats, managing access controls, and ensuring compliance with data protection regulations."
  },
  {
    "name": "Data Analyst",
    "description": "Utilizes the SaaS product to gather, analyze, and interpret data to support decision-making processes and improve business outcomes."
  },
  {
    "name": "Stakeholder/Investor",
    "description": "Interested in the financial and strategic performance of the SaaS product, focusing on return on investment and market position."
  },
  {
    "name": "Compliance Officer",
    "description": "Ensures that the SaaS product and its use comply with industry regulations and standards, managing risk and advising on compliance strategies."
  }
];

export default function populateSampleData(
    option: string,
    wiTitle: ObservableValue<string>,
    wiDescription: ObservableValue<string>,
    wiAcceptanceCriteria: ObservableValue<string>,
    pName: ObservableValue<string>,
    pDescription: ObservableValue<string>,
    pTechnologiesUsed: ObservableValue<string>,
    pLanguage: ObservableValue<string>,
    pcProvider: IProjectContextProvider) {

    var personas = samplePersonas;
    var dor = DefaultDefinitionOfReady;

    function setProjectContext() {
        pcProvider.setProjectContext({
            name: pName.value,
            description: pDescription.value,
            technologiesUsed: pTechnologiesUsed.value,
            language: pLanguage.value,
            eligiblePersonas: personas,
            definitionOfReady: dor
        });
    }

    switch (option) {
        case 'bad': {
            wiTitle.value = 'E-Commerce Interface Revamp';
            wiDescription.value = 'Need better user interface for online shopping app.';
            wiAcceptanceCriteria.value = `1. Interface should be visually appealing.
2. Simplify navigation and checkout process.
3. Reduce the number of steps to complete a purchase.`;
            pName.value = 'Project MarketRevamp';
            pDescription.value = 'The aim of Project MarketRevamp is to overhaul the user interface of our e-commerce app, focusing on improving aesthetics, user navigation, and simplifying the shopping process.';
            pTechnologiesUsed.value = 'UI/UX Design, Front-end Development, Mobile and Web Platforms';
            pLanguage.value = "English";
            break;
        }
        case 'better': {
            wiTitle.value = 'Integrated Smart Home System';
            wiDescription.value = 'As a tech-savvy homeowner, I want to control all my smart home devices from a single app to streamline my home management.';
            wiAcceptanceCriteria.value = `1. App compatibility with a wide range of smart home devices.
2. Real-time control and monitoring of devices.
3. User-friendly setup for device integration.
4. Customizable profiles for different family members.
5. Provision for manual override of automated settings.`;
            pName.value = 'Project OneHome';
            pDescription.value = 'Project OneHome focuses on developing a unified app for smart home management, enabling control over various devices like thermostats, lights, and security systems. Emphasis on user-friendly interfaces and broad device compatibility.';
            pTechnologiesUsed.value = 'IoT, Mobile App Development, API Integration, Cross-platform Compatibility';
            pLanguage.value = "English";
            break;
        }
        case 'better-still': {
            wiTitle.value = 'Advanced Wellness Tracker';
            wiDescription.value = `As a health-conscious user, I want a comprehensive app that tracks various aspects of my health and provides actionable insights.`;
            wiAcceptanceCriteria.value = `1. Comprehensive tracking of physical activities, sleep, and diet.
2. Seamless integration with health devices for data syncing.
3. AI-driven personalized health advice based on user data.
4. Goal setting and progress tracking features.
5. Secure and private handling of personal health data.`;
            pName.value = 'Project TotalWellness';
            pDescription.value = `Project TotalWellness is an initiative to enhance our health app, offering a holistic approach to health and wellness tracking. Incorporating AI for personalized insights and focusing on secure data management.`;
            pTechnologiesUsed.value = 'Mobile Development, AI, Data Analytics, Cloud Computing, Privacy and Security Technologies';
            pLanguage.value = "English";
            break;
        }
        case 'better-still-french': {
            wiTitle.value = 'Suivi Avancé du Bien-être';
            wiDescription.value = `En tant qu'utilisateur soucieux de sa santé, je souhaite une application complète qui suit divers aspects de ma santé et fournit des perspectives exploitables.`;
            wiAcceptanceCriteria.value = `1. Suivi complet des activités physiques, du sommeil et de l'alimentation.
2. Intégration transparente avec les appareils de santé pour la synchronisation des données.
3. Conseils de santé personnalisés pilotés par l'IA, basés sur les données de l'utilisateur.
4. Fonctionnalités de définition d'objectifs et de suivi des progrès.
5. Gestion sécurisée et privée des données de santé personnelles.`;
            pName.value = ' Projet TotalBienêtre';
            pDescription.value = `Le projet TotalBienêtre est une initiative visant à améliorer notre application de santé, en proposant une approche holistique du suivi de la santé et du bien-être. Incorporant l'IA pour des insights personnalisés et se concentrant sur la gestion sécurisée des données.`;
            pTechnologiesUsed.value = 'Développement Mobile, IA, Analytique des Données, Informatique en Nuage, Technologies de la Confidentialité et de la Sécurité';
            pLanguage.value = "French";
            break;
        }
        default: {
          personas = [];
        }
    }

    setProjectContext();
}

export function syncProjectContext(option: string, pcProvider: IProjectContextProvider) {
  populateSampleData(
    option,
    new ObservableValue<string>(null),
    new ObservableValue<string>(null),
    new ObservableValue<string>(null),
    new ObservableValue<string>(null),
    new ObservableValue<string>(null),
    new ObservableValue<string>(null),
    new ObservableValue<string>(null),
    pcProvider);
}